import { useState, useRef } from 'react';
import cn from 'bem-cn';

import { MetrikaGoal, useMetrika } from '../../../hooks';
import { Switch, Stack, Divider, HighlightedText, Markdown, Video } from '../../UI';

import { PromptInfoBodyProps } from './PromptInfoBody.types';
import './PromptInfoBody.scss';
import { getGuideText, getSwitchOptions, PROMPT_RESULT, PROMPT_TEXT } from './PromptInfoBody.helpers';
import { useVideoControl } from './PromptInfoBody.hooks';

const b = cn('prompt-info-body');

export const PromptInfoBody = (props: PromptInfoBodyProps) => {
    const { data, needSendMetrics = true } = props;
    const { promptForExample, example, promptImage, exampleImage, exampleVideo, isMediaPrompt } = data;

    const [switchValue, setSwitchValue] = useState(PROMPT_TEXT);
    const [sentMetrics, setSentMetrics] = useState<Partial<Record<MetrikaGoal, boolean>>>({});
    const videoRef = useRef<HTMLVideoElement>(null);
    const { reachGoal } = useMetrika();

    const isPromptOption = switchValue === PROMPT_TEXT;
    const switchOptions = getSwitchOptions(isMediaPrompt);
    const guideText = getGuideText(isPromptOption);

    const handleSwitchChange = (value: string) => {
        setSwitchValue(value);

        if (!needSendMetrics) {
            return;
        }

        const metrikaGoal = MetrikaGoal.ClickPromptResult;

        if (value === PROMPT_RESULT && !sentMetrics[metrikaGoal]) {
            // Отправляем цель один раз, повторные переключения на результат избыточны
            reachGoal(metrikaGoal);
            setSentMetrics(prev => ({ ...prev, [metrikaGoal]: true }));
        }
    };

    useVideoControl(videoRef, !isPromptOption && isMediaPrompt, exampleVideo);

    return (
        <Stack spacing={24} className={b()}>
            <Switch className={b('switch')} options={switchOptions} value={switchValue} onChange={handleSwitchChange} />
            {!isMediaPrompt && (
                <>
                    <p className={b('guide')} dangerouslySetInnerHTML={{ __html: guideText }} />
                    <Divider />
                </>
            )}
            <Stack className={b('content')}>
                {promptImage && (
                    <img
                        className={b('image', { hidden: !isPromptOption || !isMediaPrompt })}
                        src={promptImage}
                        alt=""
                    />
                )}
                {exampleImage && (
                    <img
                        className={b('image', { hidden: isPromptOption || !isMediaPrompt })}
                        src={exampleImage}
                        alt=""
                    />
                )}
                {exampleVideo && isMediaPrompt && (
                    <Video
                        ref={videoRef}
                        className={b('video', { hidden: isPromptOption })}
                        src={exampleVideo}
                        poster={promptImage}
                        autoPlay
                        muted
                        loop
                        playsInline
                        disablePictureInPicture
                    />
                )}
                {((isPromptOption && promptForExample) || (!isPromptOption && example)) && (
                    <div className={b('text-content')}>
                        {isPromptOption ? (
                            <HighlightedText useMarkdown>{promptForExample}</HighlightedText>
                        ) : (
                            <Markdown>{example}</Markdown>
                        )}
                    </div>
                )}
            </Stack>
        </Stack>
    );
};
